<template>
    <v-container fluid class="dashboard-container pa-4">
        <!-- Page Title -->
        <v-row>
            <v-col cols="12">
                <div class="d-flex align-center">
                    <h1 class="text-left">Dashboard</h1>
                    <v-btn icon="mdi-refresh" size="small" variant="text" class="ms-2"
                           :loading="loading" @click="refreshDashboard"/>
                    <span v-if="lastRefreshAgo" class="text-caption text-grey ms-2">Updated {{ lastRefreshAgo }}</span>
                </div>
            </v-col>
        </v-row>

        <!-- No Company Selected (admin/super_admin) -->
        <v-row v-if="noCompanySelected" class="mb-2">
            <v-col cols="12">
                <v-alert type="info" variant="tonal" rounded="lg" density="compact">
                    Please select a company from the top menu to view fleet insights.
                </v-alert>
            </v-col>
        </v-row>

        <!-- Morning Briefing Banner -->
        <v-row v-if="!loading && insights?.morning_briefing" class="mb-2">
            <v-col cols="12">
                <v-alert type="info" variant="tonal" rounded="lg" density="compact" closable>
                    {{ insights.morning_briefing }}
                </v-alert>
            </v-col>
        </v-row>
        <v-row v-if="loading" class="mb-2">
            <v-col cols="12">
                <v-skeleton-loader type="text"/>
            </v-col>
        </v-row>

        <!-- Payment Warning Banner -->
        <v-row v-if="isCompanyAdmin && subscriptionStore.needsAttention" class="mb-2">
            <v-col cols="12">
                <v-alert :type="subscriptionStore.isOverdue ? 'error' : 'warning'" variant="tonal" rounded="lg"
                         density="compact" class="cursor-pointer" @click="openInNewTab('invoices')">
                    <div class="d-flex align-center">
                        <span>{{ subscriptionStore.statusMessage }}</span>
                        <v-spacer/>
                        <v-btn size="small" variant="text" class="text-decoration-underline">
                            View Invoices
                        </v-btn>
                    </div>
                </v-alert>
            </v-col>
        </v-row>

        <!-- Getting Started Checklist -->
        <v-row v-if="onboardingStore.showChecklist" class="mb-2">
            <v-col cols="12" md="6">
                <getting-started-checklist
                    @activateDevice="openInNewTab('devices')"
                    @addAsset="openInNewTab('assets')"
                    @addDriver="openInNewTab('drivers')"
                    @addLocation="openInNewTab('locations')"
                    @viewLiveMap="navigateAndTrack('live-view', 'live_map')"
                    @viewTrips="navigateAndTrack('trips-report', 'trips')"
                />
            </v-col>
        </v-row>

        <!-- ═══════════ CRITICAL ═══════════ -->
        <h2 class="text-start mt-4 mb-2">Critical</h2>
        <v-row>
            <!-- Fleet Health -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Fleet Health" icon="mdi-heart-pulse" icon-color="red"
                              :count="insights?.fleet_health?.score != null ? insights.fleet_health.score + '%' : null"
                              :count-color="fleetHealthColor">
                    <div class="d-flex flex-column align-center">
                        <div style="min-width: 120px; max-width: 140px;">
                            <Doughnut :data="fleetHealthChartData" :options="doughnutOptions"/>
                        </div>
                        <div class="d-flex flex-column align-center ga-1 mt-2">
                            <div class="d-flex justify-center ga-3 flex-wrap">
                                <span class="text-caption">
                                    <v-icon size="10" color="#4CAF50" class="mr-1">mdi-circle</v-icon>
                                    Operational: {{ insights?.fleet_health?.operational ?? 0 }}
                                </span>
                                <span class="text-caption">
                                    <v-icon size="10" color="#F44336" class="mr-1">mdi-circle</v-icon>
                                    Non-reporting: {{ insights?.fleet_health?.non_reporting ?? 0 }}
                                </span>
                            </div>
                            <div class="d-flex justify-center ga-3 flex-wrap">
                                <span class="text-caption">
                                    <v-icon size="10" color="#FF9800" class="mr-1">mdi-circle</v-icon>
                                    Low Battery: {{ insights?.fleet_health?.low_battery ?? 0 }}
                                </span>
                                <span class="text-caption">
                                    <v-icon size="10" color="#FFC107" class="mr-1">mdi-circle</v-icon>
                                    MIL Active: {{ insights?.fleet_health?.mil_active ?? 0 }}
                                </span>
                            </div>
                            <div class="text-caption font-weight-medium mt-1">
                                {{ insights?.fleet_health?.operational ?? 0 }} of
                                {{ insights?.fleet_health?.total ?? 0 }} operational
                            </div>
                        </div>
                    </div>
                </insight-card>
            </v-col>

            <!-- Devices Offline -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Devices Offline" icon="mdi-truck-alert" icon-color="red"
                              :count="insights?.vehicles_down?.count" linkable @titleClick="openAssetsFiltered('non_reporting')">
                    <div class="scrollable-list">
                        <div v-if="!insights?.vehicles_down?.assets?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            All vehicles reporting
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.vehicles_down.assets" :key="a.asset_id" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(a.asset_id)">
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ a.last_seen }} &bull; {{ a.address || 'Unknown location' }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Check Engine -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Check Engine" icon="mdi-engine" icon-color="orange"
                              :count="insights?.check_engine?.count" count-color="orange" linkable @titleClick="openAssetsFiltered('check_engine')">
                    <div class="scrollable-list">
                        <div v-if="!insights?.check_engine?.assets?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            No check engine lights
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.check_engine.assets" :key="a.asset_id" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(a.asset_id, {tab: 4})">
                                <template v-slot:append>
                                    <v-chip size="x-small" color="orange" variant="tonal">
                                        {{ a.pending_dtcs }} DTC{{ a.pending_dtcs !== 1 ? 's' : '' }}
                                    </v-chip>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- DTC Codes -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="DTC Codes" icon="mdi-car-wrench" icon-color="orange"
                              :count="insights?.dtc_codes?.count" count-color="orange" linkable @titleClick="openAssetsFiltered('check_engine')">
                    <div class="scrollable-list">
                        <div v-if="!insights?.dtc_codes?.assets?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            No DTC codes
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.dtc_codes.assets" :key="a.asset_id" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(a.asset_id, {tab: 4})">
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    <v-chip v-for="code in a.dtc_list" :key="code" size="x-small" color="orange"
                                            variant="outlined" class="mr-1 mt-1">
                                        {{ code }}
                                    </v-chip>
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Low Battery -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Low Battery" icon="mdi-battery-low" icon-color="orange"
                              :count="insights?.low_battery?.count" count-color="orange" linkable @titleClick="openAssetsFiltered('low_battery')">
                    <div class="scrollable-list">
                        <div v-if="!insights?.low_battery?.devices?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            All batteries healthy
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="d in insights.low_battery.devices" :key="d.slno" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(d.asset_id)">
                                <div class="d-flex align-center justify-center ga-2">
                                    <span class="text-body-2 font-weight-medium text-primary">{{ d.label }}</span>
                                    <span class="text-caption text-medium-emphasis">
                                        {{ d.voltage ? strOperations().formatVoltage(d.voltage) : '' }}
                                    </span>
                                    <!-- battery_type is the selected channel's power-source type
                                         (api #1380); the old keys were never on this payload. -->
                                    <v-icon size="18" v-bind="getBatteryDisplay(d.battery_bars, d.battery_type ?? d.battery1_type ?? d.battery2_type)"/>
                                </div>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Crash Alerts -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Crash Alerts (24h)" icon="mdi-car-emergency" icon-color="red"
                              :count="insights?.crash_alerts?.count">
                    <div class="scrollable-list">
                        <div v-if="!insights?.crash_alerts?.alerts?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            <v-icon color="green" class="mr-1">mdi-check-circle</v-icon>
                            All clear
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.crash_alerts.alerts" :key="a.asset_alert_id ?? (a.asset_id + a.event_timestamp)"
                                         class="px-1 cursor-pointer" @click="openNotificationsInNewTab({asset: a.asset_id, alert_type: 8})">
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ a.title }} &bull; {{ formatTimeAgo(a.event_timestamp) }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- SOS / Tamper (type 13). An older API has no sos_alerts key: hide the tile then. -->
            <v-col v-if="loading || insights?.sos_alerts" cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="SOS / Tamper (24h)" :icon="alertTypeIcon(ALERT_TYPE.SOS)" icon-color="red"
                              :count="insights?.sos_alerts?.count" linkable
                              @titleClick="openNotificationsInNewTab({alert_type: ALERT_TYPE.SOS})">
                    <div class="scrollable-list">
                        <div v-if="!insights?.sos_alerts?.alerts?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            <v-icon color="green" class="mr-1">mdi-check-circle</v-icon>
                            All clear
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.sos_alerts.alerts" :key="a.asset_alert_id ?? (a.asset_id + a.event_timestamp)"
                                         class="px-1 cursor-pointer"
                                         @click="openNotificationsInNewTab({asset: a.asset_id, alert_type: ALERT_TYPE.SOS})">
                                <template v-slot:prepend>
                                    <v-icon :icon="sosRowIcon(a)" :color="sosRowColor(a)" size="small" class="me-2"/>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ sosRowLabel(a) }} &bull; {{ formatTimeAgo(a.event_timestamp) }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>
        </v-row>

        <!-- ═══════════ OPERATIONAL ═══════════ -->
        <h2 class="text-start mt-6 mb-2">Operational</h2>
        <v-row>
            <!-- Fleet Activity -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Fleet Activity" icon="mdi-car-multiple" icon-color="primary">
                    <v-row dense class="mt-1">
                        <v-col cols="4">
                            <div class="fleet-tile fleet-tile--moving text-center pa-2 rounded-lg cursor-pointer"
                                 @click="openAndTrack('live-view', 'live_map')">
                                <v-icon size="24" color="white">mdi-car</v-icon>
                                <div class="text-h5 font-weight-bold text-white mt-1">{{ insights?.fleet_activity?.moving ?? 0 }}</div>
                                <div class="text-caption text-white">Moving</div>
                            </div>
                        </v-col>
                        <v-col cols="4">
                            <div class="fleet-tile fleet-tile--idle text-center pa-2 rounded-lg cursor-pointer"
                                 @click="openAndTrack('live-view', 'live_map')">
                                <v-icon size="24" color="white">mdi-timer-sand</v-icon>
                                <div class="text-h5 font-weight-bold text-white mt-1">{{ insights?.fleet_activity?.idle ?? 0 }}</div>
                                <div class="text-caption text-white">Idle</div>
                            </div>
                        </v-col>
                        <v-col cols="4">
                            <div class="fleet-tile fleet-tile--parked text-center pa-2 rounded-lg cursor-pointer"
                                 @click="openAndTrack('live-view', 'live_map')">
                                <v-icon size="24" color="white">mdi-parking</v-icon>
                                <div class="text-h5 font-weight-bold text-white mt-1">{{ insights?.fleet_activity?.parked ?? 0 }}</div>
                                <div class="text-caption text-white">Parked</div>
                            </div>
                        </v-col>
                    </v-row>
                </insight-card>
            </v-col>

            <!-- Speed Alerts -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Speed Alerts (24h)" icon="mdi-speedometer" icon-color="purple"
                              :count="insights?.speed_alerts?.count" count-color="purple">
                    <div class="scrollable-list">
                        <div v-if="!insights?.speed_alerts?.alerts?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            No speed alerts
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.speed_alerts.alerts" :key="a.asset_id" class="px-1 cursor-pointer"
                                         @click="openNotificationsInNewTab({asset: a.asset_id, alert_type: 2})">
                                <template v-slot:append>
                                    <v-chip size="x-small" color="purple" variant="tonal">{{ a.alert_count }}</v-chip>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    Last: {{ formatTimeAgo(a.last_alert) }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Geofence Breaches -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Geofence & Places (24h)" icon="mdi-map-marker-alert"
                              :icon-color="alertTypeColor(ALERT_TYPE.GEOFENCE)"
                              :count="insights?.geofence_breaches?.count" count-color="orange">
                    <div class="scrollable-list">
                        <div v-if="!insights?.geofence_breaches?.alerts?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            No geofence or place alerts
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.geofence_breaches.alerts"
                                         :key="a.asset_alert_id ?? `${a.asset_id}-${a.alert_type || ''}-${a.event_timestamp}`" class="px-1 cursor-pointer"
                                         @click="openNotificationsInNewTab(geofenceRowQuery(a))">
                                <template v-if="a.alert_type" v-slot:prepend>
                                    <v-icon :icon="alertTypeIcon(a.alert_type)" :color="alertTypeColor(a.alert_type)"
                                            size="small" class="me-2"/>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ a.title }} &bull; {{ formatTimeAgo(a.event_timestamp) }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Unread Alerts -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Unread Alerts" icon="mdi-bell-alert" icon-color="red"
                              :count="insights?.unread_alerts?.total" count-color="red"
                              class="cursor-pointer" @click="openNotificationsInNewTab({unread: 1})">
                    <div class="d-flex flex-wrap ga-2">
                        <div v-if="!insights?.unread_alerts?.by_type?.length"
                             class="text-center text-green font-weight-medium pa-2 w-100">
                            All caught up!
                        </div>
                        <v-chip v-for="t in insights?.unread_alerts?.by_type" :key="t.type"
                                :color="alertTypeColor(t.type)" size="small" variant="tonal">
                            {{ alertTypeLabel(t.type) || t.name }}: {{ t.count }}
                        </v-chip>
                    </div>
                </insight-card>
            </v-col>

            <!-- Top Idling -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Top Idling (7d)" icon="mdi-timer-sand" icon-color="amber">
                    <div class="scrollable-list">
                        <div v-if="!insights?.top_idling?.assets?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            No idling data
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="(a, idx) in insights.top_idling.assets" :key="a.asset_id" class="px-1 cursor-pointer"
                                         @click="openTripsReportInNewTab(a.asset_id)">
                                <template v-slot:prepend>
                                    <span class="text-caption font-weight-bold mr-2 text-medium-emphasis">
                                        #{{ idx + 1 }}
                                    </span>
                                </template>
                                <template v-slot:append>
                                    <span class="text-caption font-weight-medium">
                                        {{ formatDuration(a.total_idle_sec) }}
                                    </span>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ a.idle_events }} idle event{{ a.idle_events !== 1 ? 's' : '' }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Driver Compliance -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Driver Compliance" icon="mdi-account-clock" icon-color="red"
                              :count="insights?.driver_compliance?.violations?.length || 0">
                    <div class="scrollable-list">
                        <div v-if="!insights?.driver_compliance?.violations?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            <v-icon color="green" class="mr-1">mdi-check-circle</v-icon>
                            All compliant
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="v in insights.driver_compliance.violations" :key="v.driver_id"
                                         class="px-1 cursor-pointer" @click="openInNewTab('driver-report')">
                                <template v-slot:append>
                                    <v-chip size="x-small" color="red" variant="tonal">
                                        {{ v.total_hours }}h
                                    </v-chip>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ v.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">{{ v.date }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Maintenance / Service Due -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Maintenance / Service Due" icon="mdi-wrench-clock" icon-color="amber"
                              :count="insights?.maintenance_due?.count" count-color="amber" linkable @titleClick="openAssetsFiltered('pending_maintenance')">
                    <div class="scrollable-list">
                        <div v-if="!insights?.maintenance_due?.tasks?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            No pending maintenance
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="(t, idx) in insights.maintenance_due.tasks" :key="idx" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(t.asset_id, {tab: t.type === 'inspection' ? 0 : 1})">
                                <template v-slot:append>
                                    <v-chip size="x-small"
                                            :color="t.type === 'inspection' ? 'blue' : 'amber'"
                                            variant="tonal">
                                        {{ t.type }}
                                    </v-chip>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ t.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">{{ t.task }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Unassigned Drivers -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Unassigned Drivers" icon="mdi-account-question" icon-color="blue-grey"
                              :count="insights?.unassigned_drivers?.count" count-color="blue-grey" linkable @titleClick="openAssetsFiltered('no_driver')">
                    <div class="scrollable-list">
                        <div v-if="!insights?.unassigned_drivers?.assets?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            All drivers assigned
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="a in insights.unassigned_drivers.assets" :key="a.asset_id" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(a.asset_id, {edit: 1})">
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ a.label }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Driver Safety -->
            <v-col cols="12" sm="6" md="3">
                <v-skeleton-loader v-if="safetyLoading" type="card"/>
                <insight-card v-else title="Driver Safety (30d)" icon="mdi-shield-account" icon-color="teal"
                              :count="safetyAnalytics?.fleet_avg_score != null ? Number(safetyAnalytics.fleet_avg_score).toFixed(1) : null"
                              :count-color="safetyScoreColor" linkable @titleClick="openInNewTab('fleet/reports/driver-safety')">
                    <div v-if="!safetyAnalytics || !safetyAnalytics.total_drivers"
                         class="text-center text-medium-emphasis pa-2 text-caption">
                        No driver safety data
                    </div>
                    <div v-else class="d-flex flex-column ga-1">
                        <div class="d-flex justify-space-between text-caption">
                            <span class="text-medium-emphasis">Drivers rated</span>
                            <span class="font-weight-medium">{{ safetyAnalytics.total_drivers }}</span>
                        </div>
                        <div class="d-flex justify-space-between text-caption">
                            <span class="text-medium-emphasis">Speeding events</span>
                            <span class="font-weight-medium" :class="safetyAnalytics.event_totals?.speeding > 0 ? 'text-orange' : ''">
                                {{ safetyAnalytics.event_totals?.speeding ?? 0 }}
                            </span>
                        </div>
                        <div class="d-flex justify-space-between text-caption">
                            <span class="text-medium-emphasis">Harsh braking</span>
                            <span class="font-weight-medium" :class="safetyAnalytics.event_totals?.harsh_braking > 0 ? 'text-orange' : ''">
                                {{ safetyAnalytics.event_totals?.harsh_braking ?? 0 }}
                            </span>
                        </div>
                        <div class="d-flex justify-space-between text-caption">
                            <span class="text-medium-emphasis">Hard acceleration</span>
                            <span class="font-weight-medium" :class="safetyAnalytics.event_totals?.hard_acceleration > 0 ? 'text-orange' : ''">
                                {{ safetyAnalytics.event_totals?.hard_acceleration ?? 0 }}
                            </span>
                        </div>
                    </div>
                </insight-card>
            </v-col>
        </v-row>

        <!-- ═══════════ FINANCIAL & SUMMARY ═══════════ -->
        <h2 class="text-start mt-6 mb-2">Financial &amp; Summary</h2>
        <v-row>
            <!-- Daily Summary -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Daily Summary" icon="mdi-chart-timeline-variant" icon-color="primary"
                              count-color="primary">
                    <v-row dense>
                        <v-col cols="4" class="text-center cursor-pointer" @click="openInNewTab('trips-report', {}, {type: 'daily'})">
                            <div class="text-h6 font-weight-bold text-primary">{{ insights?.daily_summary?.trips ?? 0 }}</div>
                            <div class="text-caption text-medium-emphasis">Trips</div>
                        </v-col>
                        <v-col cols="4" class="text-center cursor-pointer" @click="openInNewTab('trips-report', {}, {type: 'daily'})">
                            <div class="text-h6 font-weight-bold text-primary">
                                {{ formatDistance(insights?.daily_summary?.distance_km) }}
                            </div>
                            <div class="text-caption text-medium-emphasis text-capitalize">
                                {{ configs?.distance_unit || 'miles' }}
                            </div>
                        </v-col>
                        <v-col cols="4" class="text-center cursor-pointer" @click="openInNewTab('trips-report', {}, {type: 'daily'})">
                            <div class="text-h6 font-weight-bold text-primary">
                                {{ formatDuration(insights?.daily_summary?.drive_time_sec) }}
                            </div>
                            <div class="text-caption text-medium-emphasis">Drive Time</div>
                        </v-col>
                        <v-col cols="4" class="text-center mt-2 cursor-pointer" @click="openInNewTab('trips-report', {}, {type: 'daily'})">
                            <div class="text-h6 font-weight-bold text-primary">
                                {{ formatDuration(insights?.daily_summary?.idle_time_sec) }}
                            </div>
                            <div class="text-caption text-medium-emphasis">Idle Time</div>
                        </v-col>
                        <v-col cols="4" class="text-center mt-2 cursor-pointer" @click="openInNewTab('summary-report')">
                            <div class="text-h6 font-weight-bold text-primary">
                                {{ insights?.daily_summary?.active_vehicles ?? 0 }}
                            </div>
                            <div class="text-caption text-medium-emphasis">Active Vehicles</div>
                        </v-col>
                        <v-col cols="4" class="text-center mt-2 cursor-pointer" @click="openInNewTab('driver-report')">
                            <div class="text-h6 font-weight-bold text-primary">
                                {{ insights?.daily_summary?.active_drivers ?? 0 }}
                            </div>
                            <div class="text-caption text-medium-emphasis">Active Drivers</div>
                        </v-col>
                    </v-row>
                </insight-card>
            </v-col>

            <!-- Fuel Summary -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Fuel Summary (7d)" icon="mdi-gas-station" icon-color="teal"
                              linkable @titleClick="openInNewTab('fleet/reports/assets-fuel-refill')">
                    <div class="d-flex justify-space-around mb-3">
                        <div class="text-center">
                            <div class="text-h6 font-weight-bold">
                                ${{ insights?.fuel_summary?.total_cost?.toFixed(2) ?? '0.00' }}
                            </div>
                            <div class="text-caption text-medium-emphasis">Total Cost</div>
                        </div>
                        <div class="text-center">
                            <div class="text-h6 font-weight-bold">
                                {{ insights?.fuel_summary?.total_quantity?.toFixed(1) ?? '0.0' }}
                            </div>
                            <div class="text-caption text-medium-emphasis">Gallons</div>
                        </div>
                    </div>
                    <div v-if="insights?.fuel_summary?.top_spenders?.length" class="scrollable-list">
                        <div class="text-caption text-medium-emphasis mb-1">Top Spenders</div>
                        <v-list density="compact" class="pa-0">
                            <v-list-item v-for="s in insights.fuel_summary.top_spenders" :key="s.asset_id" class="px-1 cursor-pointer"
                                         @click="openAssetInNewTab(s.asset_id, {tab: 2})">
                                <template v-slot:append>
                                    <span class="text-caption font-weight-medium">${{ s.cost.toFixed(2) }}</span>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ s.label }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>

            <!-- Subscription Warnings -->
            <v-col cols="12" sm="6" md="4">
                <v-skeleton-loader v-if="loading" type="card"/>
                <insight-card v-else title="Subscriptions" icon="mdi-calendar-alert" icon-color="amber"
                              :count="insights?.subscription_warnings?.count" count-color="amber">
                    <div class="scrollable-list">
                        <div v-if="!insights?.subscription_warnings?.devices?.length"
                             class="text-center text-green font-weight-medium pa-2">
                            All subscriptions current
                        </div>
                        <v-list v-else density="compact" class="pa-0">
                            <v-list-item v-for="d in insights.subscription_warnings.devices" :key="d.slno" class="px-1 cursor-pointer"
                                         @click="openInNewTab('invoices')">
                                <template v-slot:append>
                                    <v-chip size="x-small" :color="subscriptionChipColor(d.days_remaining)"
                                            variant="tonal">
                                        {{ d.days_remaining }}d
                                    </v-chip>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium text-primary">{{ d.label }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    Expires: {{ d.expires }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </insight-card>
            </v-col>
        </v-row>

        <!-- ═══════════ TRIP CHARTS (preserved) ═══════════ -->
        <h2 class="text-start mt-6 mb-2">Trip Statistics</h2>
        <v-row>
            <v-col cols="12" md="6">
                <v-card elevation="3" class="rounded pa-3">
                    <h3 class="text-start mb-2">Trips Count</h3>
                    <div style="height: 250px;">
                        <Bar :data="tripCountChartData" :options="{responsive: true, maintainAspectRatio: false}"/>
                    </div>
                    <div class="text-left mt-2">
                        <v-btn-group color="primary" density="compact" divided variant="outlined">
                            <v-btn :class="{active: tripCountSelectedPeriod==='weekly'}" color="pink"
                                   @click="tripCountSelectedPeriod='weekly'">
                                Weekly
                            </v-btn>
                            <v-btn :class="{active: tripCountSelectedPeriod==='monthly'}" color="purple"
                                   @click="tripCountSelectedPeriod='monthly'">
                                Monthly
                            </v-btn>
                            <v-btn :class="{active: tripCountSelectedPeriod==='yearly'}" color="cyan"
                                   @click="tripCountSelectedPeriod='yearly'">
                                Yearly
                            </v-btn>
                        </v-btn-group>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card elevation="3" class="rounded pa-3">
                    <h3 class="text-start mb-2">Distance</h3>
                    <div style="height: 250px;">
                        <Bar :data="tripDistanceChartData" :options="distanceChartOptions"/>
                    </div>
                    <div class="text-left mt-2">
                        <v-btn-group color="primary" density="compact" divided variant="outlined">
                            <v-btn :class="{active: tripDistanceSelectedPeriod==='weekly'}" color="pink"
                                   @click="tripDistanceSelectedPeriod='weekly'">
                                Weekly
                            </v-btn>
                            <v-btn :class="{active: tripDistanceSelectedPeriod==='monthly'}" color="purple"
                                   @click="tripDistanceSelectedPeriod='monthly'">
                                Monthly
                            </v-btn>
                            <v-btn :class="{active: tripDistanceSelectedPeriod==='yearly'}" color="cyan"
                                   @click="tripDistanceSelectedPeriod='yearly'">
                                Yearly
                            </v-btn>
                        </v-btn-group>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Bottom spacer -->
        <div class="py-6"/>
    </v-container>
</template>

<script setup>
import {Bar, Doughnut} from "vue-chartjs";
import InsightCard from "./InsightCard.vue";
import GettingStartedChecklist from "./GettingStartedChecklist.vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {strOperations, useNavigation} from "@/composables/globals";
import {useSubscriptionStore} from "@/stores/subscriptionStore";
import {useOnboardingStore} from "@/stores/onboardingStore";
import pahoMqttClient from '@/plugins/mqttClient';
import {fetchMqttToken, msUntilRefresh, companyTopicPrefix} from "@/composables/mqttAuth";
import {powerSourceIcon} from "@/composables/usePowerSourceIcon";
import {ALERT_TYPE, alertColor, alertIcon, alertLabel, alertTypeColor, alertTypeIcon, alertTypeLabel} from "@/composables/alertTypes";
import {
    Chart as ChartJS,
    Title, Tooltip, Legend,
    BarElement, CategoryScale, LinearScale,
    ArcElement, DoughnutController
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, DoughnutController);

const {user, configs} = useAuthStore();
const router = useRouter();
const {navigateTo} = useNavigation();
const {showSnackbar} = useSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const onboardingStore = useOnboardingStore();

const navigateAndTrack = (routeName, milestone) => {
    if (milestone === 'live_map') onboardingStore.markLiveMapViewed();
    else if (milestone === 'trips') onboardingStore.markTripsViewed();
    router.push({name: routeName});
};

// Like navigateAndTrack but opens in a new tab, so the Fleet Activity card
// behaves like every other dashboard count-card (which all open their target
// in a new tab).
const openAndTrack = (routeName, milestone) => {
    if (milestone === 'live_map') onboardingStore.markLiveMapViewed();
    else if (milestone === 'trips') onboardingStore.markTripsViewed();
    const route = router.resolve({name: routeName});
    window.open(route.href, '_blank');
};

const isCompanyAdmin = computed(() => {
    return user?.type === 'company' && user?.role?.toLowerCase()?.includes('admin');
});

const openAssetInNewTab = (assetId, query = {}) => {
    if (!assetId) return;
    const route = router.resolve({name: 'asset', params: {asset: assetId}, query});
    window.open(route.href, '_blank');
};

const openNotificationsInNewTab = (query = {}) => {
    const route = router.resolve({name: 'notifications', query});
    window.open(route.href, '_blank');
};

const openTripsReportInNewTab = (assetId) => {
    if (!assetId) return;
    const route = router.resolve({name: 'trips-report', params: {asset: assetId}});
    window.open(route.href, '_blank');
};

const openInNewTab = (routeName, query = {}, params = {}) => {
    const route = router.resolve({name: routeName, query, params});
    window.open(route.href, '_blank');
};
const openAssetsFiltered = (fleetStatus) => {
    const route = router.resolve({name: 'assets', query: {fleet_status: fleetStatus}});
    window.open(route.href, '_blank');
};

const isAdminUser = computed(() => ['admin', 'super_admin'].includes(user?.type));
const hasCompany = computed(() => !!user?.company?.id);
const noCompanySelected = computed(() => isAdminUser.value && !hasCompany.value);

// ── Fleet Insights State ──
const insights = ref(null);
const loading = ref(true);
const safetyAnalytics = ref(null);
const safetyLoading = ref(true);
const lastRefreshTime = ref(null);
const lastRefreshAgo = ref('');
let autoRefreshTimer = null;
let timeAgoTimer = null;
let mqttDebounceTimer = null;
const mqttTopicPrefix = companyTopicPrefix(user?.company?.id);
let mqttRefreshTimer = null;

// ── Trip Chart State (preserved) ──
const tripCountDataset = ref(null);
const tripDistanceDataset = ref(null);
const tripCountSelectedPeriod = ref('weekly');
const tripDistanceSelectedPeriod = ref('weekly');

const distanceChartOptions = ref({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        tooltip: {
            callbacks: {
                label: function (context) {
                    const unit = configs?.distance_unit === 'km' ? 'km' : 'mi';
                    return `${context.raw} ${unit}`;
                }
            }
        }
    },
    scales: {
        y: {
            ticks: {
                callback: function (value) {
                    const unit = configs?.distance_unit === 'km' ? 'km' : 'mi';
                    return `${value} ${unit}`;
                }
            }
        }
    }
});

// ── Doughnut chart options ──
const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '65%',
    plugins: {
        legend: {display: false},
        tooltip: {enabled: true}
    }
};

// ── Computed: Fleet Health Doughnut ──
const fleetHealthColor = computed(() => {
    const score = insights.value?.fleet_health?.score ?? 0;
    if (score >= 80) return 'green';
    if (score >= 50) return 'amber';
    return 'red';
});

const fleetHealthChartData = computed(() => {
    const fh = insights.value?.fleet_health;
    if (!fh) return {labels: [], datasets: [{data: [1], backgroundColor: ['#e0e0e0']}]};
    return {
        labels: ['Operational', 'Non-reporting', 'Low Battery', 'MIL Active'],
        datasets: [{
            data: [fh.operational, fh.non_reporting, fh.low_battery, fh.mil_active],
            backgroundColor: ['#4CAF50', '#F44336', '#FF9800', '#FFC107'],
            borderWidth: 0
        }]
    };
});


// ── Trip Chart Computed (preserved logic) ──
const tripCountChartData = computed(() => {
    if (tripCountDataset.value) {
        return generateChartData(tripCountDataset.value, tripCountSelectedPeriod.value, 'count');
    }
    return {labels: [], datasets: [{data: [], backgroundColor: '#673AB7', label: 'Data is loading'}]};
});

const tripDistanceChartData = computed(() => {
    if (tripDistanceDataset.value) {
        return generateChartData(tripDistanceDataset.value, tripDistanceSelectedPeriod.value, 'distance');
    }
    return {labels: [], datasets: [{data: [], backgroundColor: '#673AB7', label: 'Data is loading'}]};
});

// ── Helpers ──
const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

const formatDistance = (km) => {
    if (!km) return '0';
    const val = parseFloat(strOperations().unitifyDistance(km, 'distance', 'km'));
    return isNaN(val) ? '0' : val.toFixed(0);
};

const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp + 'Z'); // API timestamps are UTC
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
};

const getBatteryDisplay = (bars, type = null) => {
    return powerSourceIcon(bars, type)
};

// SOS tile rows carry a flat sub_type; tamper and panic read differently.
const sosRow = (a) => ({alert_type: ALERT_TYPE.SOS, sub_type: a?.sub_type});
const sosRowLabel = (a) => alertLabel(sosRow(a));
const sosRowIcon = (a) => alertIcon(sosRow(a));
const sosRowColor = (a) => alertColor(sosRow(a));

// Rows carry alert_type (1 geofence, 10 place) on a newer API; an older API
// sends geofence rows only, so default to 1. The notifications list filters on
// one type, so the row's own type is the right filter.
const geofenceRowQuery = (a) => ({asset: a.asset_id, alert_type: a.alert_type ?? ALERT_TYPE.GEOFENCE});

const safetyScoreColor = computed(() => {
    // Driver scores are on a 1-10 scale.
    const score = Number(safetyAnalytics.value?.fleet_avg_score ?? 0);
    if (score >= 8) return 'teal';
    if (score >= 5) return 'amber';
    return 'red';
});

const subscriptionChipColor = (days) => {
    if (days < 7) return 'red';
    if (days < 14) return 'amber';
    return 'green';
};

// ── Chart generation (preserved from original) ──
const getWeekNumber = (date) => {
    const firstJan = new Date(date.getFullYear(), 0, 1);
    return Math.ceil((((date - firstJan) / 86400000) + firstJan.getDay() + 1) / 7);
};

const generateChartData = (dataset, selectedPeriod, valueKey) => {
    let chartData = [];
    let chartLabels = [];
    const now = new Date();

    if (selectedPeriod === 'weekly') {
        for (let i = 8; i >= 1; i--) {
            const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
            const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
            const formattedStartDate = weekStart.toLocaleDateString('en', {day: 'numeric', month: 'short'});
            const formattedEndDate = weekEnd.toLocaleDateString('en', {day: 'numeric', month: 'short'});
            const label = `${formattedStartDate} - ${formattedEndDate}`;
            const currentYear = weekStart.getFullYear();
            const currentWeek = getWeekNumber(weekStart);
            const foundEntry = dataset.weekly?.find(entry => entry.year === currentYear && entry.week === currentWeek);
            chartLabels.push(label);
            const rawVal = foundEntry ? foundEntry[valueKey] : 0;
            chartData.push(valueKey === 'distance' ? parseFloat(strOperations().unitifyDistance(rawVal, '', 'km')) || 0 : rawVal);
        }
    } else if (selectedPeriod === 'monthly') {
        for (let i = 11; i >= 0; i--) {
            const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const label = `${monthDate.toLocaleDateString('en', {month: 'short'})} ${monthDate.getFullYear()}`;
            const foundEntry = dataset.monthly?.find(entry =>
                entry.year === monthDate.getFullYear() && entry.month === monthDate.getMonth() + 1
            );
            chartLabels.push(label);
            const rawVal = foundEntry ? foundEntry[valueKey] : 0;
            chartData.push(valueKey === 'distance' ? parseFloat(strOperations().unitifyDistance(rawVal, '', 'km')) || 0 : rawVal);
        }
    } else if (selectedPeriod === 'yearly') {
        const currentYear = now.getFullYear();
        for (let i = 4; i >= 0; i--) {
            const year = currentYear - i;
            const foundEntry = dataset.yearly?.find(entry => entry.year === year);
            chartLabels.push(year.toString());
            const rawVal = foundEntry ? foundEntry[valueKey] : 0;
            chartData.push(valueKey === 'distance' ? parseFloat(strOperations().unitifyDistance(rawVal, '', 'km')) || 0 : rawVal);
        }
    }

    return {
        labels: chartLabels,
        datasets: [{
            data: chartData,
            backgroundColor: selectedPeriod === 'weekly' ? '#E91E63' : (selectedPeriod === 'monthly' ? '#673AB7' : '#009688'),
            label: selectedPeriod.toUpperCase(),
        }]
    };
};

// ── API Calls ──
const fetchInsights = () => {
    if (noCompanySelected.value) {
        loading.value = false;
        return;
    }
    const params = {};
    if (isAdminUser.value) {
        params.company = user.company.id;
    }
    params.local_hour = new Date().getHours();
    send('get', 'dashboard/fleet-insights', params)
        .then(async (response) => {
            insights.value = response.data.data;
            loading.value = false;

            // Auto-detect existing devices/assets/drivers/locations for onboarding checklist
            if (onboardingStore.hasCompletedSetup && !onboardingStore.isAllComplete) {
                const deviceCount = insights.value?.fleet_health?.total ?? 0;
                const assetCount = insights.value?.fleet_activity?.total ?? 0;
                // Fetch driver and location counts for auto-detection
                let driverCount = 0;
                let locationCount = 0;
                try {
                    const [driverRes, locationRes] = await Promise.all([
                        !onboardingStore.hasAddedDriver ? send('get', 'companies/drivers?list=true') : Promise.resolve(null),
                        !onboardingStore.hasAddedLocation && user?.company?.id ? send('get', `companies/locations/${user.company.id}`, {list: true}) : Promise.resolve(null),
                    ]);
                    if (driverRes?.data?.data) {
                        const driverData = driverRes.data.data;
                        driverCount = Array.isArray(driverData) ? driverData.length : (driverData.data?.length ?? 0);
                    }
                    if (locationRes?.data?.data) {
                        const locData = locationRes.data.data;
                        locationCount = Array.isArray(locData) ? locData.length : (locData.data?.length ?? 0);
                    }
                } catch (_) {}
                onboardingStore.detectExistingData(deviceCount, assetCount, driverCount, locationCount);
            }
        })
        .catch(() => {
            loading.value = false;
            showSnackbar('Fleet insights could not be loaded', 'error');
        });
};

const refreshDashboard = () => {
    loading.value = true;
    insights.value = null;
    safetyLoading.value = true;
    safetyAnalytics.value = null;
    fetchInsights();
    fetchTripsDataset();
    fetchTripDistanceDataset();
    fetchSafetyAnalytics();
    lastRefreshTime.value = Date.now();
};

const fetchTripsDataset = () => {
    send('get', 'reports/trips-count?chart=1')
        .then((response) => {
            tripCountDataset.value = response.data.data;
        })
        .catch(() => {});
};

const fetchTripDistanceDataset = () => {
    send('get', 'reports/trips-distance?chart=1')
        .then((response) => {
            tripDistanceDataset.value = response.data.data;
        })
        .catch(() => {});
};

const fetchSafetyAnalytics = () => {
    if (noCompanySelected.value) {
        safetyLoading.value = false;
        return;
    }
    const params = {};
    if (isAdminUser.value) {
        params.company = user.company.id;
    }
    send('get', 'drivers/safety-analytics', params)
        .then((response) => {
            safetyAnalytics.value = response.data.data;
            safetyLoading.value = false;
        })
        .catch(() => {
            safetyLoading.value = false;
        });
};

const updateTimeAgo = () => {
    if (!lastRefreshTime.value) {
        lastRefreshAgo.value = '';
        return;
    }
    const seconds = Math.floor((Date.now() - lastRefreshTime.value) / 1000);
    if (seconds < 10) lastRefreshAgo.value = 'just now';
    else if (seconds < 60) lastRefreshAgo.value = `${seconds}s ago`;
    else lastRefreshAgo.value = `${Math.floor(seconds / 60)}m ago`;
};

// ── MQTT ──
const scheduleMqttRefresh = () => {
    if (mqttRefreshTimer) clearTimeout(mqttRefreshTimer);
    mqttRefreshTimer = setTimeout(connectMqtt, msUntilRefresh() || 1500000);
};

const connectMqtt = async () => {
    if (!process.env.VUE_APP_MQTT_URL) return;
    if (!mqttTopicPrefix) return; // no active company → no company-scoped feed
    try {
        if (pahoMqttClient.client && pahoMqttClient.client.isConnected()) {
            pahoMqttClient.disconnect();
        }
    } catch (e) { /* ignore */ }

    let auth;
    try {
        auth = await fetchMqttToken();
    } catch (err) {
        console.error('Dashboard MQTT: could not obtain access token', err);
        return;
    }

    const clientId = `dashboard-${Math.random().toString(16).slice(2)}`;
    pahoMqttClient.connect(auth.wsUrl || process.env.VUE_APP_MQTT_URL, clientId, {
        username: auth.token,
        password: 'jwt',
        onSuccess: () => {
            console.log('Dashboard MQTT: connected');
            pahoMqttClient.subscribe(`${mqttTopicPrefix}#`);
            scheduleMqttRefresh();
        }
    });
    setupMqttMessageHandler();
};

const setupMqttMessageHandler = () => {
    pahoMqttClient.client.onMessageArrived = () => {
        // Trigger-only: schedule debounced refresh on any device message
        if (mqttDebounceTimer) clearTimeout(mqttDebounceTimer);
        mqttDebounceTimer = setTimeout(() => {
            if (document.visibilityState === 'visible' && !loading.value) {
                console.log('Dashboard MQTT: debounced refresh');
                refreshDashboard();
            }
        }, 30000);
    };
};

const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
        // Reconnect MQTT if disconnected
        if (process.env.VUE_APP_MQTT_URL) {
            if (!pahoMqttClient.client || !pahoMqttClient.client.isConnected()) {
                console.log('Dashboard MQTT: tab visible - reconnecting');
                connectMqtt();
            }
        }
        refreshDashboard();
    }
};

// ── Lifecycle ──
onMounted(() => {
    fetchInsights();
    fetchTripsDataset();
    fetchTripDistanceDataset();
    fetchSafetyAnalytics();
    lastRefreshTime.value = Date.now();

    // Connect MQTT for real-time refresh triggers
    connectMqtt();

    // Auto-refresh every 60s when tab is visible
    autoRefreshTimer = setInterval(() => {
        if (document.visibilityState === 'visible' && !loading.value) {
            refreshDashboard();
        }
    }, 60000);

    // Update "X ago" every 5s
    timeAgoTimer = setInterval(updateTimeAgo, 5000);

    document.addEventListener('visibilitychange', onVisibilityChange);
});

onBeforeUnmount(() => {
    if (autoRefreshTimer) clearInterval(autoRefreshTimer);
    if (timeAgoTimer) clearInterval(timeAgoTimer);
    if (mqttDebounceTimer) clearTimeout(mqttDebounceTimer);
    if (mqttRefreshTimer) clearTimeout(mqttRefreshTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (process.env.VUE_APP_MQTT_URL) {
        if (mqttTopicPrefix) pahoMqttClient.unsubscribe(`${mqttTopicPrefix}#`);
        pahoMqttClient.disconnect();
        console.log('Dashboard MQTT: disconnected');
    }
});
</script>

<style scoped>
.dashboard-container {
    overflow-y: auto;
    height: calc(100vh - 64px);
}

.scrollable-list {
    max-height: 200px;
    overflow-y: auto;
}

.fleet-tile {
    transition: transform 0.15s, box-shadow 0.15s;
}
.fleet-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.fleet-tile--moving { background-color: #4CAF50; }
.fleet-tile--idle   { background-color: #FFC107; }
.fleet-tile--parked { background-color: #9E9E9E; }
</style>
